import { ApolloCache, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Input } from "reactstrap";
import { GET_ORG_POSITIONS, GET_POSITION_DETAIL } from "../../../../graphql/position-gql";
import useScope from "../../../../hook/useScope";
import { notifySuccess } from "../../../../services/notification";
import { Action, IIndividual, IPositionAssignmentLight } from "../../../../types/individual";
import confirm from "../../../common/confirm";
import DateFormat from "../../../common/date-format";
import { CancelIcon, CheckMarkIcon, DeleteIcon } from "../../../common/icons";
import Info from "../../../common/info";
import SpinnerOverlay from "../../../common/spinner/spinner";
import { ASSIGN_INDIVIDUAL, INDIVIDUAL_ASSIGNMENTS } from "../../../../graphql/individual-gql";
import { IPosition } from "../../../../types/position";
import { fullNameAutoFormat } from "../../../../utils/format-fullname";
import { GET_ORG_DETAILS } from "../../../../graphql/org-gql";
import useAuth from "../../../../hook/useAuth";
import Button from "../../../common/button";

type NonHomePositionIndividualProp = {
    position:IPosition;
    //selectedTreeItem: any ;
}
const NonHomePositionIndividual = (prop:NonHomePositionIndividualProp) => {

  const {position} = prop;

  const [indi,SetIndi] = useState<IIndividual>()
  const [ updateAssignment,{loading} ] = useMutation(ASSIGN_INDIVIDUAL);
  const [acting,setActing] = useState<boolean>(false)
  const isScope = useScope(position?.orgId||0,false);
  const [getOrgDetails,{data:orgDetailsResponse}] = useLazyQuery(GET_ORG_DETAILS);
  const { isFrenchTranslator } = useAuth();
  const [cloned,setCloned] = useState<boolean>(false);

  useEffect(()=>{
    setCloned(!!position?.shareLinkId)
    if(position?.orgId) getOrgDetails({ variables: { id: position.orgId||0} });
  },[position])
  useEffect(()=>{

    const noHomeIndi = position?.individuals?.filter(indi=>indi.relType.code !== 'DIRECT');
    if(noHomeIndi&&noHomeIndi.length!==0) SetIndi(noHomeIndi[0])
    else SetIndi(undefined)

  },[position])

  useEffect(()=>{

    indi&&setActing(indi.acting)

  },[indi])

  const getRefetchQuery = () => {
    return [
      {query: GET_POSITION_DETAIL, variables: { positionId: position.positionId}},
      {query: INDIVIDUAL_ASSIGNMENTS, variables: { individualId: indi?.individualId}},
    ]
  }

  const onEndAssignmentHandle = () =>{
    endAssignmentConfirm().then(()=>{
      endAssignmentExecute()
    })
  }

  const endAssignmentConfirm = () => {
    return confirm({
      title: 'Warning!',
      content: `Are you sure you want to end <b>${fullNameAutoFormat(indi?.firstName,indi?.middleName,indi?.lastName)}'</b> non-home (${indi?.assignmentType}) assignment from <b>${position.fullPositionName}</b>?`,
      okText: <><DeleteIcon/> End Assignment</>,
      okBtnColor: 'danger'
    })
  }

  const endAssignmentExecute = ()=>{
    updateAssignment({
      variables: {
        positionId:position?.positionId||0,
        action:Action.REMOVAL,
        input:{assignmentId: indi?.positionAssignmentId}
      },
      update(cache,{data}){
          //console.log("data:",data)
          const cachedOrg = cache.readQuery<{
              org:{
                  positions:Array<IPosition>
              }
          }>({
              query: GET_ORG_POSITIONS,
              variables: { orgId: position?.orgId},
            });

          if(!cachedOrg) return

          let updatedPositions = [...cachedOrg.org.positions];
          const updatedPositionIndex = updatedPositions.findIndex(
              (pos: IPosition) => pos.positionId === position.positionId
            );

          if (updatedPositionIndex !== -1) {
              const indiIndex = updatedPositions[updatedPositionIndex].individuals.findIndex(
                (individual:IIndividual) => individual.individualId === indi?.individualId
              )
              if(indiIndex!==-1){
                let updatedIndividuals = [...updatedPositions[updatedPositionIndex].individuals]
                updatedIndividuals.splice(indiIndex, 1);
                updatedPositions[updatedPositionIndex]={
                  ...updatedPositions[updatedPositionIndex],
                  individuals: updatedIndividuals
                };
              }
          }
          // Update the cached data with the modified positions array
          cache.writeQuery({
              query: GET_ORG_POSITIONS,
              variables: { orgId: position?.orgId },
              data: {
                  org: {
                      ...cachedOrg.org,
                      positions: updatedPositions,
                  },
              },
          });

          SetIndi(undefined);


      },
      refetchQueries: getRefetchQuery(),
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        data && notifySuccess()
      }
    })

  }

  const actingDisable = () =>{
    //console.log("indi:",indi)
    return (indi?.assignmentType!=='Secondment'&&indi?.assignmentType!=='Link'&&indi?.assignmentType!=='Fixed Term')||cloned
  }

  const actingConfirm = (check:boolean) => {
    const content = check ? `This individual's assignment will show as "Acting". Do you want to continue?`
        : `"Acting" will be removed from the individual's assignment, do you want to continue?`
    return confirm({
      title: 'Warning!',
      content:  content,
      okText: <> Confirm</>,
      okIcon: <CheckMarkIcon color="white"/>
    })
  }

  const actingCheckboxChangeHandle = () => {
    setActing(!acting)
    actingConfirm(!acting).then(()=>{
      updateAssignment({
        variables: {
          positionId:position.positionId,
          action:Action.ACTING,
          input:{assignmentId: indi?.positionAssignmentId,acting:!acting}
        },
        update(cache){
          updatePositionCacheForActing(cache)
          updateAssignmentsCacheForActing(cache)
        },
        onCompleted: (data) => {
          data && notifySuccess()
        }
      })
    })
  }

  const updatePositionCacheForActing = (cache:ApolloCache<any>) => {
    const cachedOrg = cache.readQuery<{
      org:{
          positions:Array<IPosition>
      }
    }>({
      query: GET_ORG_POSITIONS,
      variables: { orgId: position?.orgId},
    });
    if(cachedOrg){

      let updatedPositions = [...cachedOrg.org.positions];
      const updatedPositionIndex = updatedPositions.findIndex(
          (pos: IPosition) => pos.positionId === position.positionId
        );

      if (updatedPositionIndex !== -1) {
          const indiIndex = updatedPositions[updatedPositionIndex].individuals.findIndex(
            (individual:IIndividual) => individual.individualId === indi?.individualId
          )
          if(indiIndex!==-1){
            let updatedIndividuals = [...(updatedPositions[updatedPositionIndex].individuals)]
            let updatedIndividual={...(updatedIndividuals[indiIndex]),acting:!acting};
            updatedIndividuals.splice(indiIndex, 1);
            updatedPositions[updatedPositionIndex]={
              ...updatedPositions[updatedPositionIndex],
              individuals: [...updatedIndividuals,updatedIndividual]
            };
          }
      }
      // Update the cached data with the modified positions array
      cache.writeQuery({
          query: GET_ORG_POSITIONS,
          variables: { orgId: position?.orgId },
          data: {
              org: {
                  ...cachedOrg.org,
                  positions: updatedPositions,
              },
          },
      });
    }
  }

  const updateAssignmentsCacheForActing = (cache:ApolloCache<any>) => {
    const assignmentsCache = cache.readQuery<{
      individualAssignments:IPositionAssignmentLight[]
    }>({
      query: INDIVIDUAL_ASSIGNMENTS,
      variables: { individualId: indi?.individualId},
    });
    if(!assignmentsCache) return;

      let updateAssignments = [...assignmentsCache.individualAssignments];
      const updatedAssignmentIndex = updateAssignments.findIndex(
          (assignment: IPositionAssignmentLight) => assignment.assignmentId===indi?.positionAssignmentId
      );

      if (updatedAssignmentIndex !== -1) {
        let updatedAssignment={...(updateAssignments[updatedAssignmentIndex]),acting:!acting};
        updateAssignments[updatedAssignmentIndex]=  updatedAssignment ;
        console.log("updateAssignments:",updateAssignments)
      }
      // Update the cached data with the modified assignments array
      cache.writeQuery({
          query: INDIVIDUAL_ASSIGNMENTS,
          variables: { individualId: indi?.individualId},
          data: {
            individualAssignments: [...updateAssignments]
          },
      });
  }

  return (
    <div role="region" aria-label="Individual Information" className="NoHomePositionIndividual">
      {loading  && <SpinnerOverlay />}
      {(!indi)&&
        <Info>There is no one currently assigned to this non-home position.</Info>
      }

      {(indi)&&
        <>
        { !isFrenchTranslator &&
         <> <Button color="danger" style={{ marginRight: '15px' }} onClick={onEndAssignmentHandle} disabled={!isScope||cloned}>
              <DeleteIcon /> End Assignment
          </Button>
          <br/><br/>
          </>
        }
          <div className='row'>
            <div className="col-md-6 flex-container" >
              <div className="flex-item">
                <div style={{fontWeight:"bold"}}>Individual Name:</div>
                <div style={{fontWeight:"bold"}}>Related Organization:</div>
                <div style={{fontWeight:"bold"}}>Acting position?</div>
              </div>
              <div className="flex-item" style={{flexGrow:'1'}}>
                <div id="name" >{fullNameAutoFormat(indi?.firstName,indi?.middleName,indi?.lastName)}</div>
                <div id="relatedOrg" >{orgDetailsResponse?.org?.orgName||' '}</div>
                <div id="acting" >
                  {!isFrenchTranslator && <Input type="checkbox" disabled={actingDisable()} checked={acting||false} onChange={() => actingCheckboxChangeHandle() } />}
                  {isFrenchTranslator && acting && <CheckMarkIcon title="Acting"></CheckMarkIcon>}
                  {isFrenchTranslator && !acting && <CancelIcon></CancelIcon>}
                </div>
              </div>
            </div>

            <div className="col-md-6 flex-container" >
              <div className="flex-item">
                <div style={{fontWeight:"bold"}}>Start Date:</div>
                <div style={{fontWeight:"bold"}}>Assignment Type:</div>
                <div>&nbsp;  </div>
              </div>
              <div className="flex-item" style={{flexGrow:'1'}}>
                <div id="startDate" ><DateFormat date={indi?.startDate} defaultText="No Date" /></div>
                <div id="relType" >
                  <span>{indi?.relType?.label}</span>
                  <span>{acting&&' (Acting)'}</span>
                </div>
                <div>&nbsp;  </div>
              </div>
            </div>
          </div>
        </>
      }
    </div>)

}

export default NonHomePositionIndividual
